import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import { API } from '../config';
import {
  dataEntrata, ordinaEntroFascia, aggregaRegioni, provinceDiRegione,
} from './esportabiliData';
import ExportComposer from '../components/export/ExportComposer';
import { IconExport } from '../components/Icons';
import './esportabili.css';

// Tre colori FISSI (non legati al tema): con l'accento arancio di default P2/P3
// diventerebbero indistinguibili. var(--info) e' il blu semantico del progetto,
// stabile in chiaro/scuro e mai ridefinito dai temi (vedi theme-tokens.css).
// P1 verde, P2 blu, P3 arancione. Niente viola, mai.
const FASCE = [
  { k: 'ftth', n: 'P1 · Nuova fibra', c: 'var(--ok)', d: 'passati a fibra, TIM compresi' },
  { k: 'nuovo', n: 'P2 · Nuovi', c: 'var(--info)', d: 'mai lavorati' },
  { k: 'scaduto', n: 'P3 · Da ricontattare', c: '#e08a1e', d: "timing scaduto dall'ultima esportazione" },
];
const COL = { ftth: 'p1', nuovo: 'p2', scaduto: 'p3' };
const fmt = (n) => (n ?? 0).toLocaleString('it-IT');
const fmtData = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT') : '—');

// Etichetta TIM/NON TIM (banda 3): due colori semantici DEDICATI, gia' nel design
// system (theme-tokens.css), stabili in chiaro/scuro — non i colori delle fasce.
const RIS_COLOR = {
  TIM: { bg: 'rgba(239,68,68,.12)', fg: 'var(--color-tim, var(--err))' },
  'NON TIM': { bg: 'rgba(34,197,94,.12)', fg: 'var(--color-non-tim, var(--ok))' },
};

// Ordinamento lato client della lista (banda 3): GET /esportabili/lista arriva già
// scoped al pool e diviso per fascia (una fetch per priorita'), qui si ordina DENTRO
// ciascuna fascia con ordinaEntroFascia (fibra prima del rame, aziende prima dei
// privati, a parita' il piu' vecchio — vedi esportabiliData.js e i suoi test).
// NOTA: /esportabili/lista non offre ancora questo ordinamento server-side sull'intero
// pool (arrivera' in SP3 insieme al creatore di lotto): si ordinano i soli record
// caricati (fino a LIMIT_LISTA per fascia).
const LIMIT_LISTA = 200;

export default function EsportabiliPage() {
  const [rie, setRie] = useState(null);
  const [rieLoading, setRieLoading] = useState(true);
  const [rieErr, setRieErr] = useState(false);

  // territorio (banda 2): righeProvince = tutte le province (livello base, caricato
  // UNA SOLA VOLTA), da cui si ricavano le regioni per somma (aggregaRegioni). I comuni
  // di una provincia si caricano solo quando quella provincia viene espansa, e restano
  // in cache per provincia (comuni[nomeProvincia]) cosi' non si ricaricano ad ogni click.
  const [righeProvince, setRigheProvince] = useState([]);
  const [terrLoading, setTerrLoading] = useState(true);
  const [terrErr, setTerrErr] = useState(false);
  const [comuni, setComuni] = useState({});   // { [provincia]: { loading, err, righe } }

  const [priorita, setPriorita] = useState(null);   // 'ftth' | 'nuovo' | 'scaduto' | null
  // Selezione/drill territoriale: UN livello alla volta (regione -> provincia ->
  // comune). Il livello piu' profondo valorizzato e' anche il filtro dei record E la
  // vista corrente della colonna territorio (vedi `vista` piu' sotto) — niente piu'
  // stati separati per "espanso": qui selezionare ed entrare sono la stessa cosa.
  const [regione, setRegione] = useState(null);
  const [provincia, setProvincia] = useState(null);
  const [comune, setComune] = useState(null);
  const [sort, setSort] = useState({ key: 'totale', dir: 'desc' });

  const [lista, setLista] = useState({ items: [], totale: 0 });
  const [listaLoading, setListaLoading] = useState(true);
  const [listaErr, setListaErr] = useState(false);

  // "Manda all'export" (banda 1): finestra sovrapposta col compositore, precompilata
  // con la fascia/area correnti. refreshTick fa ripartire l'effetto della lista qui
  // sotto (stessi filtri, dati freschi) senza duplicarne la logica.
  const [composerOpen, setComposerOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // riepilogo: si carica all'apertura della pagina e si ricarica dopo ogni export. La
  // generazione del lotto NON toglie nulla dal pool (i record escono solo al PRIMO
  // SCARICAMENTO dallo Storico, vedi export_service.create_lotto_unified): il
  // ricaricamento qui non serve a "far sparire" quanto appena generato, resta comunque
  // utile per allinearsi a scaricamenti fatti nel frattempo da altri.
  const caricaRiepilogo = useCallback(() => {
    setRieLoading(true);
    return axios.get(`${API}/esportabili/riepilogo`)
      .then((r) => { setRie(r.data); setRieErr(false); })
      .catch(() => { setRie(null); setRieErr(true); })
      .finally(() => setRieLoading(false));
  }, []);
  useEffect(() => { caricaRiepilogo(); }, [caricaRiepilogo]);

  // territorio: le righe-provincia (con `reg`) si caricano all'apertura della pagina e
  // si ricaricano dopo ogni export, per lo stesso motivo del riepilogo — le regioni si
  // ricavano da queste per somma, i comuni si caricano a parte quando la provincia
  // viene espansa (vedi caricaComuni) e vanno ricaricati anch'essi se gia' aperta.
  const caricaTerritorio = useCallback(() => {
    setTerrLoading(true);
    return axios.get(`${API}/esportabili/territorio`)
      .then((r) => { setRigheProvince(r.data?.righe || []); setTerrErr(false); })
      .catch(() => { setRigheProvince([]); setTerrErr(true); })
      .finally(() => setTerrLoading(false));
  }, []);
  useEffect(() => { caricaTerritorio(); }, [caricaTerritorio]);

  // quando si sceglie una priorità, la tabella si ordina di default sulla sua colonna
  useEffect(() => {
    setSort({ key: priorita ? COL[priorita] : 'totale', dir: 'desc' });
  }, [priorita]);

  // carica (e mette in cache) i comuni di una provincia al momento dell'espansione,
  // non prima — evita di scaricare tutti i comuni d'Italia quando serve solo l'albero
  // regioni/province.
  const caricaComuni = (nomeProvincia) => {
    setComuni((c) => ({ ...c, [nomeProvincia]: { loading: true, err: false, righe: c[nomeProvincia]?.righe || [] } }));
    axios.get(`${API}/esportabili/territorio?provincia=${encodeURIComponent(nomeProvincia)}`)
      .then((r) => setComuni((c) => ({ ...c, [nomeProvincia]: { loading: false, err: false, righe: r.data?.righe || [] } })))
      .catch(() => setComuni((c) => ({ ...c, [nomeProvincia]: { loading: false, err: true, righe: [] } })));
  };

  // banda 3 — lista dei record del pool: GET /esportabili/lista parte da
  // export_priorita.query_pool() (stessa definizione unica di tessere e territorio,
  // col paracadute su chi non ha ancora un export_motivo scritto), quindi totale e
  // filtri qui non possono piu' divergere dal resto della pagina. Provincia E comune
  // sono filtri di query (lato server): niente troncamento client-side.
  // /esportabili/lista non conosce la REGIONE (nessuna modifica al backend): quando e'
  // selezionata solo la regione si interroga una provincia alla volta (le province
  // della regione si ricavano da righeProvince con provinceDiRegione) e si concatenano
  // i risultati — il totale resta esatto perche' e' una somma di conteggi server-side,
  // solo l'elenco mostrato resta capato a LIMIT_LISTA come nel caso senza filtri.
  // Senza priorita' selezionata servono piu' fetch (una per fascia, per provincia) per
  // poter concatenare la lista nell'ordine P1 → P2 → P3, dato che l'endpoint non
  // ordina per priorita'.
  useEffect(() => {
    setListaLoading(true);
    const motivi = priorita ? [priorita] : FASCE.map((f) => f.k);
    const province = provincia
      ? [provincia]
      : regione
        ? provinceDiRegione(righeProvince, regione).map((r) => r.nome)
        : [null];
    const richieste = motivi.flatMap((m) => province.map((prov) => {
      const params = new URLSearchParams({ priorita: m, limit: String(LIMIT_LISTA) });
      if (prov) params.set('provincia', prov);
      if (comune) params.set('comune', comune);
      return axios.get(`${API}/esportabili/lista?${params.toString()}`).then((r) => ({ motivo: m, data: r.data }));
    }));
    Promise.all(richieste)
      .then((results) => {
        let totale = 0;
        let merged = [];
        motivi.forEach((m) => {
          const perMotivo = results.filter((x) => x.motivo === m);
          totale += perMotivo.reduce((s, x) => s + (x.data?.total || 0), 0);
          const items = perMotivo.flatMap((x) => x.data?.items || []);
          merged = merged.concat(ordinaEntroFascia(items));
        });
        setLista({ items: merged.slice(0, LIMIT_LISTA), totale });
        setListaErr(false);
      })
      .catch(() => { setLista({ items: [], totale: 0 }); setListaErr(true); })
      .finally(() => setListaLoading(false));
    // righeProvince e' letto ma volutamente fuori dalle dipendenze: una regione si puo'
    // selezionare solo cliccandola nell'albero, cosa possibile solo DOPO che righeProvince
    // e' gia' stato caricato (l'albero delle regioni nasce da li'), quindi al momento in
    // cui "regione" cambia righeProvince e' gia' stabile — includerlo rifarebbe la fetch
    // una seconda volta, inutilmente, ad ogni caricamento pagina.
  }, [priorita, regione, provincia, comune, refreshTick]); // eslint-disable-line

  const tot = (k) => rie?.totali?.[k] || { totale: 0, tim: 0, non_tim: 0 };

  const clickSort = (key) => setSort((s) => (
    s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: key === 'nome' ? 'asc' : 'desc' }
  ));

  // Filtra (per la fascia selezionata) e ordina un livello dell'albero territorio.
  // Un'unica funzione per i tre livelli (regioni / province di una regione / comuni
  // di una provincia): stessi controlli di ordinamento in cima alla tabella per tutti.
  const ordina = (righe) => {
    let out = priorita ? (righe || []).filter((r) => (r[COL[priorita]] || 0) > 0) : (righe || []);
    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    return [...out].sort((a, b) => (
      key === 'nome' ? mul * a.nome.localeCompare(b.nome, 'it') : mul * ((a[key] || 0) - (b[key] || 0))
    ));
  };

  // Le tre "viste" possibili della colonna territorio — UNA alla volta, mai insieme:
  // dalle regioni si entra in una regione (-> le sue province), da li' in una provincia
  // (-> i suoi comuni). regioniComplete/provinceComplete NON passano da `ordina` (niente
  // filtro/ordinamento) perche' servono anche per i totali in intestazione quando si e'
  // gia' dentro un livello — un cambio di priorita' non deve far "sparire" il totale
  // della regione/provincia che si sta guardando.
  const regioniComplete = aggregaRegioni(righeProvince);
  const provinceComplete = regione ? provinceDiRegione(righeProvince, regione) : [];
  const vista = provincia ? 'comuni' : regione ? 'province' : 'regioni';
  const comuniStato = provincia ? comuni[provincia] : null;

  const righeVista = vista === 'regioni' ? ordina(regioniComplete)
    : vista === 'province' ? ordina(provinceComplete)
    : ordina(comuniStato?.righe);
  const vistaLoading = vista === 'comuni' ? (!comuniStato || comuniStato.loading) : terrLoading;
  const vistaErr = vista === 'comuni' ? !!comuniStato?.err : terrErr;

  // Click su una riga: entra SEMPRE nel livello sotto (mai un toggle espandi/richiudi),
  // e quel livello e' anche il nuovo filtro dei record. Il comune e' l'unico livello
  // foglia: si seleziona/deseleziona restando sulla stessa vista (i comuni non hanno
  // un livello sotto in cui entrare).
  const clickRegione = (nome) => { setRegione(nome); setProvincia(null); setComune(null); };
  const clickProvincia = (nome) => {
    setProvincia(nome); setComune(null);
    if (!comuni[nome]) caricaComuni(nome);
  };
  const clickComune = (nome) => setComune((c) => (c === nome ? null : nome));

  // "Torna indietro": un passo alla volta, come richiesto — da una provincia si torna
  // a Italia (tutte le regioni), da un comune si torna alla sua regione (le province).
  const tornaItalia = () => { setRegione(null); setProvincia(null); setComune(null); };
  const tornaRegione = () => { setProvincia(null); setComune(null); };

  const arrow = (key) => (sort.key === key ? (sort.dir === 'desc' ? ' ▼' : ' ▲') : '');

  // Percentuale di un segmento della micro-barra composizione (colonna territorio):
  // niente scorrimento orizzontale per 5 colonne in una colonna stretta, la barra
  // sostituisce P1/P2/P3 con un solo colpo d'occhio; i numeri esatti restano nel
  // title della riga.
  const pct = (v, totale) => (totale > 0 ? ((v || 0) / totale) * 100 : 0);

  // Riga della vista territorio corrente (regioni, o province di una regione, o comuni
  // di una provincia): stesso markup e stesse 4 colonne P1·P2·P3·Totale in ogni vista,
  // nessun rientro/freccetta — un solo livello e' mai visibile alla volta.
  const clickRiga = vista === 'regioni' ? clickRegione : vista === 'province' ? clickProvincia : clickComune;
  // Solo al livello foglia (comuni) una riga puo' restare "selezionata" restando sulla
  // stessa vista — regioni e province, cliccate, cambiano subito vista (la riga sparisce).
  const nomeSelezionato = vista === 'comuni' ? comune : null;
  const etichettaCol = vista === 'regioni' ? 'Regione' : vista === 'province' ? 'Provincia' : 'Comune';

  // Se e' selezionata SOLO una regione (niente provincia/comune) ma non si riescono a
  // ricavare le sue province da righeProvince (territorio non ancora caricato, o la
  // fetch e' fallita — es. dopo un export, vedi caricaTerritorio), areaDelDrill() qui
  // sotto tornerebbe {province: []}: NESSUN vincolo d'area, e il compositore si
  // aprirebbe pronto a generare un lotto di TUTTA ITALIA mentre l'utente crede di
  // essere sulla sola regione. Su un filtro d'area l'errore deve essere conservativo
  // (bloccare), mai permissivo (allargare): "Manda all'export" resta disabilitato
  // finche' il drill non e' risolvibile.
  const drillNonRisolvibile = !!regione && !provincia && !comune
    && provinceDiRegione(righeProvince, regione).length === 0;

  // Area del drill nello stesso formato accettato da ExportComposer (initial.province +
  // initial.comune): dentro un comune passa provincia E comune; in una provincia solo
  // quella; in una regione le sue province (l'endpoint /export non conosce le regioni,
  // solo regioni/province — vedi provinceDiRegione); su tutta Italia nessun vincolo.
  const areaDelDrill = () => {
    if (comune) return { province: provincia ? [provincia] : [], comune };
    if (provincia) return { province: [provincia] };
    if (regione) return { province: provinceDiRegione(righeProvince, regione).map((r) => r.nome) };
    return {};
  };

  // A generazione avvenuta (ExportComposer.onExported): l'esito e' gia' mostrato dal
  // compositore stesso (toast), qui si chiude la finestra e si ricarica TUTTA la pagina.
  // ATTENZIONE: la generazione del lotto non marca nulla, i numeri restano nel pool
  // finche' il lotto non viene scaricato dallo Storico (vedi commento su
  // caricaRiepilogo) — il ricaricamento qui NON fa sparire quanto appena generato, e'
  // innocuo (mostrera' di nuovo gli stessi numeri) ma resta utile comunque, quindi lo
  // si lascia.
  const dopoExport = () => {
    setComposerOpen(false);
    caricaRiepilogo();
    caricaTerritorio();
    if (provincia) caricaComuni(provincia);
    setRefreshTick((t) => t + 1);
  };

  return (
    <div className="esp">
      <div className="esp-head">
        <h1>Priorità export — cosa passare per primo</h1>
        <button
          type="button"
          className="btn btn-primary"
          disabled={drillNonRisolvibile}
          title={drillNonRisolvibile
            ? `Nessuna provincia con record in ${regione}: o il territorio non si e' caricato, o la regione e' rimasta senza esportabili.`
            : undefined}
          onClick={() => setComposerOpen(true)}
        >
          <IconExport /> Manda all'export
        </button>
      </div>
      {drillNonRisolvibile && (
        <div className="esp-msg esp-msg-err" style={{ marginTop: -8, marginBottom: 12 }}>
          Nessuna provincia con record in {regione}: o il territorio non si è caricato, o la regione è
          rimasta senza esportabili. L'export resta bloccato per non generare per sbaglio un lotto di
          tutta Italia.
        </div>
      )}

      {/* Banda 1 — tessere + Banda 1b — grafico ingressi / scadenze */}
      {rieLoading ? (
        <div className="esp-msg">Caricamento…</div>
      ) : rieErr ? (
        <div className="esp-msg esp-msg-err">Errore nel caricamento dei dati di riepilogo.</div>
      ) : (
        <>
          <div className="esp-tessere">
            {FASCE.map((f) => {
              const t = tot(f.k);
              const sel = priorita === f.k;
              return (
                <button key={f.k} type="button" className={`esp-tessera${sel ? ' sel' : ''}`}
                  style={{ borderColor: f.c, boxShadow: sel ? `0 0 0 3px ${f.c}33` : 'none' }}
                  onClick={() => setPriorita(sel ? null : f.k)}>
                  <div className="esp-t-nome">{f.n}</div>
                  <div className="esp-t-num">{fmt(t.totale)}</div>
                  {t.tim > 0 && <div className="esp-t-mix">{fmt(t.non_tim)} NON TIM · {fmt(t.tim)} TIM</div>}
                  <div className="esp-t-desc">{f.d}</div>
                </button>
              );
            })}
          </div>

          <div className="esp-flusso">
            <div className="esp-graf">
              <h2>Ingressi negli ultimi 30 giorni</h2>
              {/* Il grafico NON e' filtrato dalla tessera selezionata (a differenza di
                  territorio e lista record): mostra sempre tutte e tre le serie, cosi'
                  non diventa mai vuoto per effetto di un clic e non cambia mai forma
                  ne' altezza — un clic su P1/P2/P3 filtra solo territorio e record. */}
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={rie?.ingressi || []} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #e2e8f0)" vertical={false} />
                  <XAxis dataKey="giorno" tickFormatter={(g) => g.slice(8) + '/' + g.slice(5, 7)}
                    tick={{ fontSize: 10, fill: 'var(--text-secondary, #64748b)' }} interval="preserveStartEnd" />
                  {/* dominio minimo [0,5]: se in 30 giorni non e' entrato nessun record in
                      nessuna fascia, la griglia resta leggibile invece di collassare su
                      una riga sola — resta l'unico caso di vuoto reale, il grafico non
                      viene comunque mai sostituito da un messaggio */}
                  <YAxis allowDecimals={false} domain={[0, (max) => (max > 0 ? max : 5)]}
                    tick={{ fontSize: 10, fill: 'var(--text-secondary, #64748b)' }} width={34} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} labelFormatter={(g) => `Giorno ${g}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {FASCE.map((f) => (
                    <Bar key={f.k} dataKey={f.k} name={f.n} stackId="a" fill={f.c} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="esp-scad">
              <h2>In scadenza</h2>
              <p className="esp-nota">previsione</p>
              <div className="esp-scad-riga">
                {['30', '60', '90'].map((g) => (
                  <div key={g} className="esp-scad-item">
                    <strong>{fmt(rie?.in_scadenza?.[g])}</strong>
                    <span>{g} gg</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Corpo a due colonne affiancate: territorio (stretta) + record (larga), che
          riempiono insieme l'altezza rimasta sotto la banda 1 — niente più impilate,
          cliccare un'area a sinistra aggiorna i record subito accanto. Sotto i 1550px
          (vedi il media query in fondo al css) tornano a impilarsi. */}
      <div className="esp-body">
        {/* Banda 2 — territorio: UN livello alla volta (regioni, poi le province della
            regione scelta, poi i comuni della provincia scelta), dentro un riquadro ad
            altezza fissa (.esp-tablewrap): cambiare livello sostituisce l'elenco, non lo
            allunga mai. Briciola di pane per risalire di un livello alla volta. */}
        <div className="esp-terr">
          <div className="esp-terr-head">
            {vista === 'regioni' ? (
              <h2>Territorio</h2>
            ) : vista === 'province' ? (
              <>
                <button type="button" className="esp-terr-back" onClick={tornaItalia}>‹ Italia</button>
                <h2>{regione} <span className="esp-terr-tot">· {fmt(regioniComplete.find((r) => r.nome === regione)?.totale)}</span></h2>
              </>
            ) : (
              <>
                <button type="button" className="esp-terr-back" onClick={tornaRegione}>‹ {regione}</button>
                <h2>{provincia} <span className="esp-terr-tot">· {fmt(provinceComplete.find((r) => r.nome === provincia)?.totale)}</span></h2>
              </>
            )}
          </div>

          {vistaLoading ? (
            <div className="esp-msg">Caricamento…</div>
          ) : vistaErr ? (
            <div className="esp-msg esp-msg-err">
              {vista === 'comuni' ? 'Errore nel caricamento dei comuni.' : 'Errore nel caricamento del territorio.'}
            </div>
          ) : righeVista.length === 0 ? (
            <div className="esp-msg">
              {priorita
                ? `Nessuna area con record in "${FASCE.find((f) => f.k === priorita)?.n}" con i dati attuali.`
                : `Nessun dato per ${vista === 'comuni' ? 'questa provincia' : vista === 'province' ? 'questa regione' : 'il pool esportabile'}.`}
            </div>
          ) : (
            <div className="esp-tablewrap">
              <table className="esp-table esp-table-terr">
                <thead>
                  <tr>
                    <th className="l" onClick={() => clickSort('nome')}>{etichettaCol}{arrow('nome')}</th>
                    <th onClick={() => clickSort(priorita ? COL[priorita] : 'totale')}>
                      {priorita ? FASCE.find((f) => f.k === priorita)?.n.split(' · ')[0] : 'Totale'}
                      {arrow(priorita ? COL[priorita] : 'totale')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {righeVista.map((r) => (
                    <tr key={r.nome} className={nomeSelezionato === r.nome ? 'sel' : ''} onClick={() => clickRiga(r.nome)}
                      title={`${r.nome} — P1 ${fmt(r.p1)} · P2 ${fmt(r.p2)} · P3 ${fmt(r.p3)}`}>
                      <td className="l">{r.nome}</td>
                      <td className="esp-comp">
                        <span className="esp-mbar">
                          <i className={`seg p1${priorita && priorita !== 'ftth' ? ' dim' : ''}`} style={{ width: pct(r.p1, r.totale) + '%' }} />
                          <i className={`seg p2${priorita && priorita !== 'nuovo' ? ' dim' : ''}`} style={{ width: pct(r.p2, r.totale) + '%' }} />
                          <i className={`seg p3${priorita && priorita !== 'scaduto' ? ' dim' : ''}`} style={{ width: pct(r.p3, r.totale) + '%' }} />
                        </span>
                        <span className="esp-comp-num">{fmt(priorita ? r[COL[priorita]] : r.totale)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Banda 3 — lista dei record del pool, filtrata da priorità/regione/provincia/
            comune: l'intestazione dice SEMPRE cosa si sta guardando (tutta Italia, o
            l'area selezionata) e offre un modo evidente per tornare a tutta Italia. */}
        <div className="esp-lista">
          <div className="esp-lista-head">
            <div className="esp-lista-titolo">
              <h2>Record · {comune || provincia || regione || 'tutta Italia'}</h2>
              {(regione || provincia || comune) && (
                <button type="button" className="esp-lista-clear" onClick={tornaItalia}>
                  × tutta Italia
                </button>
              )}
            </div>
            {priorita && (
              <span className="esp-lista-fascia" style={{ color: FASCE.find((f) => f.k === priorita)?.c }}>
                {FASCE.find((f) => f.k === priorita)?.n}
              </span>
            )}
            {!listaLoading && !listaErr && (
              <span className="esp-lista-tot">
                {fmt(lista.items.length)} mostrati di {fmt(lista.totale)} nel pool
              </span>
            )}
          </div>

          {listaLoading ? (
            <div className="esp-msg">Caricamento…</div>
          ) : listaErr ? (
            <div className="esp-msg esp-msg-err">Errore nel caricamento della lista.</div>
          ) : lista.items.length === 0 ? (
            <div className="esp-msg">Nessun record del pool con i filtri attuali.</div>
          ) : (
            <div className="esp-tablewrap">
              <table className="esp-table esp-table-static">
                <thead>
                  <tr>
                    <th className="l">Ragione sociale</th>
                    <th className="l">Numero</th>
                    <th className="l">Comune (Prov)</th>
                    <th>TIM / NON TIM</th>
                    <th>Priorità</th>
                    <th>Tecnologia</th>
                    <th>Entrato il</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.items.map((it) => {
                    const rc = RIS_COLOR[it.risultato] || { bg: 'var(--surface-2)', fg: 'var(--muted)' };
                    const fascia = FASCE.find((f) => f.k === it.export_motivo);
                    return (
                      <tr key={it.id}>
                        <td className="l">{it.ragione_sociale || '—'}</td>
                        <td className="l"><code>{it.numero_pulito || it.numero_raw || '—'}</code></td>
                        <td className="l">{it.comune || '—'}{it.provincia_prefisso ? ` (${it.provincia_prefisso})` : ''}</td>
                        <td>
                          <span className="esp-pill" style={{ background: rc.bg, color: rc.fg }}>
                            {it.risultato || '—'}
                          </span>
                        </td>
                        <td>
                          {fascia && (
                            <span className="esp-prio" style={{ color: fascia.c }}>
                              {fascia.n.split(' · ')[0]}
                            </span>
                          )}
                        </td>
                        <td className={`esp-tec${it.copertura_tecnologia ? '' : ' ignota'}`}>
                          {it.copertura_tecnologia || 'ignota'}
                        </td>
                        <td>{fmtData(dataEntrata(it))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* "Manda all'export": stesso compositore e stesso endpoint del resto dell'app
          (ExportComposer, /export/genera) — nessun percorso di export separato. Finestra
          sovrapposta (non cambia pagina, non tocca l'altezza fissa di .esp). */}
      {composerOpen && (
        <div className="ob-overlay" onClick={() => setComposerOpen(false)}>
          <div className="xc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ob-head">
              <span>Manda all'export</span>
              <button type="button" className="ob-x" onClick={() => setComposerOpen(false)}>✕</button>
            </div>
            <div className="xc-modal-body">
              <ExportComposer
                initial={{
                  priorita: priorita ? [priorita] : FASCE.map((f) => f.k),
                  ...areaDelDrill(),
                }}
                onExported={dopoExport}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
